import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import theme from 'app/theme';
import { t } from 'i18next';
import { AppDispatch } from 'app/store';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { captureException } from '@sentry/react';
import { NavigateFunction } from 'react-router-dom';
import { graphqlOperation } from 'aws-amplify';
import { getTransactionFilterOptions } from 'graphql/queries';
import {
  executeTransactionSearch,
  executeTransactionLoadMore,
} from 'app/store/filtering/transactionFiltersThunks';
import { AutocompleteRenderInputParams, InputAdornment, TextField } from '@mui/material';
import { GetTransactionFilterOptionsQuery, AgaveGlCode, AgaveCostCode } from 'API';
const COMMMON_STYLE = {
  width: '100%',
  backgroundColor: theme.palette.primary.lighter,
  paddingLeft: '10px',
  borderRadius: '8px',
};

export const hideForMobile = { xs: 'none', sm: 'table-cell' };

export const filledTextField = (params: AutocompleteRenderInputParams) => (
  <TextField
    {...params}
    variant="standard"
    InputProps={{
      ...params.InputProps,
      startAdornment: (
        <InputAdornment position="start">
          <LabelOutlinedIcon fontSize="small" />
        </InputAdornment>
      ),
      disableUnderline: true,
      style: COMMMON_STYLE,
    }}
  />
);

export const unassignedTextField = (
  params: AutocompleteRenderInputParams,
  placeholder: string | null = 'Unassigned'
) => {
  return (
    <TextField
      {...params}
      sx={{ input: { '&::placeholder': { opacity: 1 } } }}
      placeholder={placeholder ?? t('unassigned')}
      variant="standard"
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <LabelOutlinedIcon fontSize="small" />
          </InputAdornment>
        ),
        disableUnderline: true,
        style: COMMMON_STYLE,
      }}
    />
  );
};

export const typeToSearchTextField = (params: AutocompleteRenderInputParams) => {
  return (
    <TextField
      {...params}
      sx={{ input: { '&::placeholder': { opacity: 1 } } }}
      placeholder={t('typeToSearch')}
      variant="standard"
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <InputAdornment position="start">
            <LabelOutlinedIcon fontSize="small" />
          </InputAdornment>
        ),
        disableUnderline: true,
        style: COMMMON_STYLE,
      }}
    />
  );
};

export interface LoadTransactionsProps {
  dispatch: AppDispatch;
  _nextToken?: string;
  selectedPaidolId: string;
  pageName: string;
  limit?: number;
}

export const loadTransactionsDispatcher = ({
  dispatch,
  _nextToken,
  selectedPaidolId,
  pageName,
  limit,
}: LoadTransactionsProps) => {
  // SPE-3541: load/load-more only needs the selected paidol; neither
  // executeTransactionSearch nor executeTransactionLoadMore consume
  // financialAccountId. Gating on it coupled the transaction list to the async
  // integration load, which could leave the list unfetched. Gate on
  // selectedPaidolId alone (see the init effects in TransactionsV2 /
  // AgaveTransactions).
  if (selectedPaidolId) {
    if (!_nextToken) {
      dispatch(executeTransactionSearch({ pageName, limit }));
    } else {
      dispatch(
        executeTransactionLoadMore({
          pageName,
          nextToken: _nextToken,
          limit,
        })
      );
    }
  }
};

export interface RefreshTransactionsPageProps {
  onlyMyTransactions?: boolean;
  userIsAdministrator?: boolean;
  navigate: NavigateFunction;
}

export const refreshTransactionsPage = ({
  onlyMyTransactions,
  userIsAdministrator,
  navigate,
}: RefreshTransactionsPageProps) => {
  if (onlyMyTransactions) {
    navigate(`/${userIsAdministrator ? 'profile' : 'overview'}/my-transactions`, { replace: true });
  } else {
    navigate(`/overview/transactions`, { replace: true });
  }
};

export const removeTypename = (obj: any): any => {
  if (Array.isArray(obj)) {
    return obj.map(removeTypename);
  } else if (obj !== null && typeof obj === 'object') {
    const { __typename, ...rest } = obj;
    return Object.keys(rest).reduce((acc, key) => {
      acc[key] = removeTypename(rest[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};

export const showQBDFullName = (AgaveGlCode: AgaveGlCode | AgaveCostCode | undefined | null): string => {
  if (!AgaveGlCode) return '';

  let dataQBD: any = {};

  try {
    const sourceData = AgaveGlCode.source_data?.data;
    if (typeof sourceData === 'string' && sourceData.trim().length > 0) {
      dataQBD = JSON.parse(sourceData);
    }
  } catch (error) {
    captureException(error, { extra: { AgaveGlCode } });
    dataQBD = {};
  }

  return typeof dataQBD.FullName === 'string' && dataQBD.FullName.trim().length > 0
    ? dataQBD.FullName
    : AgaveGlCode.name ?? '';
};

/**
 * Generic function to deduplicate items by a specific field value while preserving all associated IDs.
 * When multiple records have the same field value, creates a single display entry
 * with all IDs stored in the relatedIds field.
 *
 * @param items - Array of items to deduplicate
 * @param groupByField - Field name to group/deduplicate by (e.g., 'code', 'label', 'name')
 * @param labelGenerator - Optional function to generate custom labels
 * @returns Array of deduplicated items with relatedIds for duplicates
 */
export const deduplicateByField = <T extends { id: string; label: string }>(
  items: T[],
  getDupeKey: (item: T) => string = (item) => item.label
): Array<T & { relatedIds?: string[] }> => {
  // group items by the key and then reduce
  const grouped = items.reduce((acc, item) => {
    const key = getDupeKey(item);
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {} as Record<string, T[]>);

  // return array of original items
  return Object.values(grouped).map((group) => {
    const ids = group.map((item) => item.id);
    return {
      ...group[0],
      ...(ids.length > 1 && { relatedIds: ids }),
    };
  });
};

interface SourceIdCollapsible {
  id: string;
  source_id?: string | null;
  relatedIds?: Array<string | null> | null;
  number?: string | null;
}

/**
 * SEE-1204: collapse rows that share a `source_id` (a migration/cutover-reconciliation
 * group, e.g. QBD automatic -> direct) to a single representative row, same grouping key
 * as the accumulator in useFilteringData.ts (~419-565). Rows without a `source_id` pass
 * through unchanged. Among a group, the representative is the row carrying `relatedIds`
 * (the backend stamps that only on the current/parent row); falling back to a row with a
 * `number` and then the first-seen row if neither signal is present.
 */
export const collapseBySourceId = <T extends SourceIdCollapsible>(items: T[]): T[] => {
  const repBySource = new Map<string, T>();
  const repTierBySource = new Map<string, number>();

  items.forEach((item) => {
    const src = (item.source_id ?? '').toString().trim();
    if (!src) return;
    const tier = (item.relatedIds ?? []).filter(Boolean).length > 0 ? 2 : item.number ? 1 : 0;
    const currentTier = repTierBySource.get(src);
    if (currentTier === undefined || tier > currentTier) {
      repTierBySource.set(src, tier);
      repBySource.set(src, item);
    }
  });

  const emittedSources = new Set<string>();
  const out: T[] = [];
  items.forEach((item) => {
    const src = (item.source_id ?? '').toString().trim();
    if (!src) {
      out.push(item);
      return;
    }
    if (emittedSources.has(src)) return;
    emittedSources.add(src);
    out.push(repBySource.get(src) as T);
  });
  return out;
};

export const printImage = (url: string) => {
  const isPDF = (() => {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.pathname.toLowerCase().endsWith('.pdf');
    } catch (error) {
      return url.toLowerCase().endsWith('.pdf');
    }
  })();

  if (isPDF) {
    // Open PDF directly in a new tab for the browsers built-in viewer
    const pdfWindow = window.open(url, '_blank');
    if (!pdfWindow) {
      alert('Pop-up blocked! Please allow pop-ups for this website.');
    }
    return;
  }

  // For images not PDFs
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Pop-up blocked! Please allow pop-ups for this website.');
    return;
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>Print Image</title>
        <style>
          body, html {
            margin: 0;
            padding: 0;
            text-align: center;
          }
          img {
            max-width: 100%;
            height: auto;
          }
        </style>
      </head>
      <body>
        <img src="${url}" alt="Print Image" onload="window.print();" />
      </body>
    </html>
  `);
  printWindow.document.close();
};

/**
 * Fetch transaction filter options from cached table.
 * This reads from a pre-computed cache for fast performance.
 */
export interface FetchTransactionFilterOptionsProps {
  selectedPaidolId: string;
  graphqlRequest: any;
}

export const fetchTransactionFilterOptions = async ({
  selectedPaidolId,
  graphqlRequest,
}: FetchTransactionFilterOptionsProps) => {
  return graphqlRequest(
    graphqlOperation(getTransactionFilterOptions, {
      paidolId: selectedPaidolId,
    })
  ) as Promise<GraphQLResult<GetTransactionFilterOptionsQuery>>;
};
